import { alpha, createTheme, Theme } from '@mui/material/styles';

/**
 * Single source of truth for the AuxTrack interface.
 *
 * Previously the palette was declared twice - once in App.tsx and once,
 * hardcoded to light mode, in MainLayout - and components reached for raw
 * hex values. Everything visual now derives from these tokens so a change
 * lands everywhere at once, which is the baseline a product needs before
 * it can be themed per customer.
 */

export const brand = {
  indigo: '#4F46E5',
  indigoLight: '#6366F1',
  indigoDark: '#4338CA',
  teal: '#0D9488',
  amber: '#D97706',
  rose: '#E11D48',
} as const;

// Shift chips are tinted per auxiliaire. They stay light in both themes -
// the text on them is dark by design - so they are defined once here rather
// than inline in each calendar view.
export const shiftPalette = [
  '#B5EAD7', '#FFDAC1', '#FFB7B2', '#C7CEEA', '#E2F0CB',
  '#FFE5D9', '#B5E6FF', '#D4A5A5', '#A2D2FF', '#FFC8DD',
] as const;

export const SHIFT_TEXT = '#26303B';

const display = '"Poppins", "Inter", system-ui, -apple-system, sans-serif';
const body = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';

export function buildTheme(isDark: boolean): Theme {
  const base = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: isDark ? brand.indigoLight : brand.indigo,
        light: brand.indigoLight,
        dark: brand.indigoDark,
        contrastText: '#FFFFFF',
      },
      secondary: { main: brand.teal, contrastText: '#FFFFFF' },
      warning: { main: brand.amber },
      error: { main: brand.rose },
      background: {
        default: isDark ? '#0B1020' : '#F7F8FC',
        paper: isDark ? '#141B2D' : '#FFFFFF',
      },
      text: isDark
        ? { primary: '#E8ECF5', secondary: '#A0AAC0', disabled: '#5C6784' }
        : { primary: '#111827', secondary: '#5B6478', disabled: '#9AA2B4' },
      divider: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(17,24,39,0.09)',
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: body,
      // A restrained scale: too many sizes is what makes an interface look
      // assembled rather than designed.
      h1: { fontFamily: display, fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em' },
      h2: { fontFamily: display, fontWeight: 700, fontSize: '1.6rem', letterSpacing: '-0.02em' },
      h3: { fontFamily: display, fontWeight: 600, fontSize: '1.3rem', letterSpacing: '-0.01em' },
      h4: { fontFamily: display, fontWeight: 600, fontSize: '1.15rem' },
      h5: { fontFamily: display, fontWeight: 600, fontSize: '1rem' },
      h6: { fontFamily: display, fontWeight: 600, fontSize: '0.95rem' },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
      caption: { fontSize: '0.75rem' },
    },
  });

  return createTheme(base, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: 'antialiased',
            // Long carer names must never force a horizontal scrollbar.
            overflowX: 'hidden',
          },
          // Respect a reader's motion preference; the app animates on every
          // route change and month step.
          '@media (prefers-reduced-motion: reduce)': {
            '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: alpha(base.palette.background.paper, isDark ? 0.8 : 0.85),
            backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${base.palette.divider}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 14 },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${base.palette.divider}`,
            boxShadow: isDark ? 'none' : '0 1px 2px rgba(17,24,39,0.04)',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 16, minHeight: 40 },
          // 44px is the smallest comfortable touch target.
          sizeLarge: { minHeight: 48 },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 40,
            paddingInline: 16,
          },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10, backgroundColor: isDark ? alpha('#fff', 0.03) : '#fff' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: base.palette.divider },
          head: {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: base.palette.text.secondary,
            backgroundColor: isDark ? alpha('#fff', 0.02) : alpha('#111827', 0.02),
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 8, fontWeight: 600 } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            // Dialogs become sheets on a phone rather than floating boxes.
            '@media (max-width:600px)': {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              borderRadius: '16px 16px 0 0',
              position: 'absolute',
              bottom: 0,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { borderRadius: 8, fontSize: '0.75rem' } },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, minHeight: 48 },
        },
      },
    },
  });
}
